import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { User, Truck, MapPin } from 'lucide-react';

const PROVIDER_TYPES = [
  { value: 'individual', icon: User, labelKey: 'auth.provider_type_individual' },
  { value: 'business_mobile', icon: Truck, labelKey: 'auth.provider_type_business_mobile' },
  { value: 'business_location', icon: MapPin, labelKey: 'auth.provider_type_business_location' },
];

const AuthPage = () => {
  // mode: 'login' | 'register' | 'verify' | 'forgot' | 'reset'
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'client',
    language: 'es',
    provider_type: 'individual',
    business_name: '',
  });
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, register, verifyEmail, resendVerification, forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isLogin = mode === 'login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData);
      }

      if (result.success) {
        toast.success(isLogin ? t('auth.login_success') : t('auth.register_success'));
        navigate('/dashboard');
      } else if (result.needsVerification) {
        // Fresh registration, or login blocked because email isn't verified yet.
        setMode('verify');
        if (!isLogin) toast.success(t('auth.register_success'));
        else toast.message(result.error);
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await verifyEmail(formData.email, code);
      if (result.success) {
        toast.success(t('auth.verify_success'));
        navigate('/dashboard');
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await resendVerification(formData.email);
      toast.success(t('auth.code_resent'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await forgotPassword(formData.email);
      if (result.success) {
        toast.success(t('auth.reset_code_sent'));
        setMode('reset');
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await resetPassword(formData.email, code, newPassword);
      if (result.success) {
        toast.success(t('auth.reset_success'));
        setCode('');
        setNewPassword('');
        setMode('login');
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isProvider = !isLogin && formData.role === 'provider';
  const isBusiness = isProvider && formData.provider_type !== 'individual';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #fff9e6 100%)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-xl" data-testid="auth-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold text-center" style={{ fontFamily: 'Outfit' }}>
              {t({
                login: 'auth.login_title',
                register: 'auth.register_title',
                verify: 'auth.verify_title',
                forgot: 'auth.forgot_title',
                reset: 'auth.reset_title',
              }[mode])}
            </CardTitle>
            <CardDescription className="text-center">
              {t({
                login: 'auth.switch_to_register',
                register: 'auth.switch_to_login',
                verify: 'auth.verify_subtitle',
                forgot: 'auth.forgot_subtitle',
                reset: 'auth.reset_subtitle',
              }[mode])}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* ── Verify activation code ─────────────────────────────── */}
            {mode === 'verify' && (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verify-code">{t('auth.code_label')}</Label>
                  <Input
                    id="verify-code"
                    data-testid="verify-code-input"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    required
                    className="h-12 text-center text-2xl tracking-[0.5em]"
                  />
                </div>
                <Button type="submit" className="w-full h-12 text-lg font-semibold" disabled={loading}
                  style={{ background: 'hsl(215 100% 34%)', borderRadius: '9999px' }}>
                  {loading ? 'Loading...' : t('auth.verify_btn')}
                </Button>
                <div className="flex items-center justify-between mt-4 text-sm">
                  <button type="button" onClick={handleResend} disabled={loading} className="text-primary hover:underline">
                    {t('auth.resend_code')}
                  </button>
                  <button type="button" onClick={() => setMode('login')} className="text-muted-foreground hover:underline">
                    {t('auth.back_to_login')}
                  </button>
                </div>
              </form>
            )}

            {/* ── Forgot password (request code) ─────────────────────── */}
            {mode === 'forgot' && (
              <form onSubmit={handleForgot} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{t('auth.email')}</Label>
                  <Input
                    id="forgot-email"
                    data-testid="forgot-email-input"
                    type="email"
                    value={formData.email}
                    onChange={e => handleChange('email', e.target.value)}
                    required
                    className="h-12"
                  />
                </div>
                <Button type="submit" className="w-full h-12 text-lg font-semibold" disabled={loading}
                  style={{ background: 'hsl(215 100% 34%)', borderRadius: '9999px' }}>
                  {loading ? 'Loading...' : t('auth.forgot_btn')}
                </Button>
                <div className="text-center mt-4">
                  <button type="button" onClick={() => setMode('login')} className="text-sm text-muted-foreground hover:underline">
                    {t('auth.back_to_login')}
                  </button>
                </div>
              </form>
            )}

            {/* ── Reset password (code + new password) ───────────────── */}
            {mode === 'reset' && (
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-code">{t('auth.code_label')}</Label>
                  <Input
                    id="reset-code"
                    data-testid="reset-code-input"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    required
                    className="h-12 text-center text-2xl tracking-[0.5em]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-password">{t('auth.new_password_label')}</Label>
                  <Input
                    id="reset-password"
                    data-testid="reset-password-input"
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    className="h-12"
                  />
                  <p className="text-xs text-muted-foreground">{t('auth.password_hint')}</p>
                </div>
                <Button type="submit" className="w-full h-12 text-lg font-semibold" disabled={loading}
                  style={{ background: 'hsl(215 100% 34%)', borderRadius: '9999px' }}>
                  {loading ? 'Loading...' : t('auth.reset_btn')}
                </Button>
                <div className="text-center mt-4">
                  <button type="button" onClick={() => setMode('login')} className="text-sm text-muted-foreground hover:underline">
                    {t('auth.back_to_login')}
                  </button>
                </div>
              </form>
            )}

            {/* ── Login / Register ───────────────────────────────────── */}
            {(mode === 'login' || mode === 'register') && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('auth.name')}</Label>
                    <Input
                      id="name"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={e => handleChange('name', e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('auth.phone')}</Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      value={formData.phone}
                      onChange={e => handleChange('phone', e.target.value)}
                      className="h-12"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={formData.email}
                  onChange={e => handleChange('email', e.target.value)}
                  required
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-xs text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      {t('auth.forgot_link')}
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={formData.password}
                  onChange={e => handleChange('password', e.target.value)}
                  required
                  className="h-12"
                />
                {!isLogin && (
                  <p className="text-xs text-muted-foreground">{t('auth.password_hint')}</p>
                )}
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="role">{t('auth.role')}</Label>
                  <Select value={formData.role} onValueChange={value => handleChange('role', value)}>
                    <SelectTrigger id="role" data-testid="role-select" className="h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">{t('auth.client')}</SelectItem>
                      <SelectItem value="provider">{t('auth.provider')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Provider type selection — shown only when role=provider on register */}
              {isProvider && (
                <div className="space-y-2">
                  <Label>{t('provider.provider_type_label')}</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {PROVIDER_TYPES.map(({ value, icon: Icon, labelKey }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleChange('provider_type', value)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left text-sm transition-all
                          ${formData.provider_type === value
                            ? 'border-primary bg-primary/5 text-primary font-medium'
                            : 'border-gray-200 hover:border-gray-400 text-foreground'}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {t(labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Business name — only for non-individual provider types */}
              {isBusiness && (
                <div className="space-y-2">
                  <Label htmlFor="business_name">{t('auth.business_name_label')}</Label>
                  <Input
                    id="business_name"
                    data-testid="business-name-input"
                    value={formData.business_name}
                    onChange={e => handleChange('business_name', e.target.value)}
                    className="h-12"
                    placeholder="e.g. López Plomería"
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-lg font-semibold"
                disabled={loading}
                data-testid="auth-submit-button"
                style={{ background: 'hsl(215 100% 34%)', borderRadius: '9999px' }}
              >
                {loading ? 'Loading...' : t(isLogin ? 'auth.login_btn' : 'auth.register_btn')}
              </Button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setMode(isLogin ? 'register' : 'login')}
                  className="text-sm text-primary hover:underline"
                  data-testid="toggle-auth-mode-button"
                >
                  {t(isLogin ? 'auth.switch_to_register' : 'auth.switch_to_login')}
                </button>
              </div>
            </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AuthPage;
